import type { TranscriptElement } from '../../../types.ts/transcript';

type TextItem = { index: number; word: string };

function normalizeWord(s: string): string {
  return s.toLowerCase().replace(/[^\w]/g, '');
}

/**
 * Find the range of transcript element indices whose text best matches the
 * given quote.
 *
 * Uses IDF-weighted word overlap so that distinctive words (names, uncommon
 * verbs) anchor the match while common words ("the", "I", "is") contribute
 * little. Bigram scoring is layered on top to reward word-order agreement.
 * Multiple window sizes accommodate AI paraphrasing that shortens or expands
 * the source text.
 */
export function findQuoteMatchRange(
  elements: TranscriptElement[],
  quote: string,
): { start: number; end: number } | null {
  const quoteWords = quote
    .split(/\s+/)
    .map(normalizeWord)
    .filter(w => w.length > 0);
  if (quoteWords.length === 0) return null;

  // Build text-item array from visible transcript elements
  const textItems: TextItem[] = [];
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (
      el.type === 'text' &&
      el.value &&
      el.state !== 'removed' &&
      el.state !== 'cut'
    ) {
      textItems.push({ index: i, word: normalizeWord(el.value) });
    }
  }
  if (textItems.length === 0) return null;

  // --- IDF weights ---
  // Count how often each word appears in the transcript so we can
  // down-weight ubiquitous words and up-weight rare/distinctive ones.
  const wordFreq = new Map<string, number>();
  for (const item of textItems) {
    wordFreq.set(item.word, (wordFreq.get(item.word) || 0) + 1);
  }

  const uniqueQuoteWords = new Set(quoteWords);
  const quoteWordWeight = new Map<string, number>();
  let maxUnigramScore = 0;
  for (const word of uniqueQuoteWords) {
    const freq = wordFreq.get(word) || 0;
    if (freq === 0) continue; // word absent from transcript
    const weight = Math.log(textItems.length / (1 + freq));
    quoteWordWeight.set(word, weight);
    maxUnigramScore += weight;
  }

  // --- Bigrams (consecutive word pairs from the quote) ---
  const quoteBigrams = new Set<string>();
  for (let i = 0; i < quoteWords.length - 1; i++) {
    quoteBigrams.add(quoteWords[i] + ' ' + quoteWords[i + 1]);
  }
  const maxBigramCount = quoteBigrams.size || 1;

  if (maxUnigramScore === 0 && quoteBigrams.size === 0) return null;

  // --- Sliding window ---
  const windowSizes = [
    Math.floor(quoteWords.length * 0.75),
    quoteWords.length,
    Math.floor(quoteWords.length * 1.5),
    Math.floor(quoteWords.length * 2),
  ].filter(s => s > 0 && s <= textItems.length);

  let bestScore = -1;
  let bestWinLeft = 0;
  let bestWinRight = 0;

  for (const winSize of windowSizes) {
    // Track word counts inside the window so we know when a quote word
    // first appears or fully leaves.
    const windowCounts = new Map<string, number>();
    let weightedUnigram = 0;
    let bigramCount = 0;

    // Seed initial window [0 .. winSize-1]
    for (let i = 0; i < winSize; i++) {
      const w = textItems[i].word;
      const prev = windowCounts.get(w) || 0;
      windowCounts.set(w, prev + 1);
      if (prev === 0 && quoteWordWeight.has(w)) {
        weightedUnigram += quoteWordWeight.get(w)!;
      }
      // bigram with previous item
      if (i > 0) {
        const bg = textItems[i - 1].word + ' ' + w;
        if (quoteBigrams.has(bg)) bigramCount++;
      }
    }

    const score = computeScore(
      weightedUnigram,
      maxUnigramScore,
      bigramCount,
      maxBigramCount,
    );
    if (score > bestScore) {
      bestScore = score;
      bestWinLeft = 0;
      bestWinRight = winSize - 1;
    }

    // Slide one position at a time
    for (let i = winSize; i < textItems.length; i++) {
      // --- add the new element entering on the right ---
      const addWord = textItems[i].word;
      const addPrev = windowCounts.get(addWord) || 0;
      windowCounts.set(addWord, addPrev + 1);
      if (addPrev === 0 && quoteWordWeight.has(addWord)) {
        weightedUnigram += quoteWordWeight.get(addWord)!;
      }
      // new bigram: (i-1, i)
      const addBg = textItems[i - 1].word + ' ' + addWord;
      if (quoteBigrams.has(addBg)) bigramCount++;

      // --- remove the element leaving on the left ---
      const remIdx = i - winSize;
      const remWord = textItems[remIdx].word;
      const remNew = (windowCounts.get(remWord) || 1) - 1;
      windowCounts.set(remWord, remNew);
      if (remNew === 0 && quoteWordWeight.has(remWord)) {
        weightedUnigram -= quoteWordWeight.get(remWord)!;
      }
      // removed bigram: (remIdx, remIdx+1)
      const remBg = remWord + ' ' + textItems[remIdx + 1].word;
      if (quoteBigrams.has(remBg)) bigramCount--;

      const s = computeScore(
        weightedUnigram,
        maxUnigramScore,
        bigramCount,
        maxBigramCount,
      );
      if (s > bestScore) {
        bestScore = s;
        bestWinLeft = i - winSize + 1;
        bestWinRight = i;
      }
    }
  }

  if (bestScore <= 0.1) return null;

  // Refine: tighten the coarse window so that the start/end align with
  // the actual beginning and ending words of the quote.
  return refineMatchBoundaries(
    textItems,
    bestWinLeft,
    bestWinRight,
    quoteWords,
  );
}

/** Combine IDF-weighted unigram overlap (70%) with bigram order score (30%). */
function computeScore(
  weightedUnigram: number,
  maxUnigram: number,
  bigramCount: number,
  maxBigram: number,
): number {
  const unigramRatio = maxUnigram > 0 ? weightedUnigram / maxUnigram : 0;
  const bigramRatio = maxBigram > 0 ? bigramCount / maxBigram : 0;
  return unigramRatio * 0.7 + bigramRatio * 0.3;
}

/**
 * After the coarse sliding-window picks the best region, tighten its
 * boundaries so that the highlighted start/end align with the actual
 * beginning and ending words of the quote.
 *
 * We look for the first few words of the quote near the left edge and the
 * last few words near the right edge. If at least half of the anchor words
 * match in sequence we snap the boundary inward.
 */
function refineMatchBoundaries(
  textItems: TextItem[],
  winLeft: number,
  winRight: number,
  quoteWords: string[],
): { start: number; end: number } {
  if (quoteWords.length < 3) {
    return { start: textItems[winLeft].index, end: textItems[winRight].index };
  }

  const anchorLen = Math.min(4, Math.floor(quoteWords.length / 2));
  const headWords = quoteWords.slice(0, anchorLen);
  const tailWords = quoteWords.slice(-anchorLen);
  const threshold = Math.max(2, Math.ceil(anchorLen * 0.5));

  let refinedLeft = winLeft;
  let refinedRight = winRight;

  // --- Refine start: scan from left looking for head anchor ---
  const headSearchEnd = winLeft + Math.floor((winRight - winLeft) / 2);
  for (let i = winLeft; i <= headSearchEnd; i++) {
    if (textItems[i].word === headWords[0]) {
      let matches = 1;
      for (let j = 1; j < headWords.length && i + j <= winRight; j++) {
        if (textItems[i + j].word === headWords[j]) matches++;
      }
      if (matches >= threshold) {
        refinedLeft = i;
        break;
      }
    }
  }

  // --- Refine end: scan from right looking for tail anchor ---
  const tailSearchStart = winRight - Math.floor((winRight - winLeft) / 2);
  for (let i = winRight; i >= tailSearchStart; i--) {
    if (textItems[i].word === tailWords[tailWords.length - 1]) {
      let matches = 1;
      for (let j = 1; j < tailWords.length && i - j >= winLeft; j++) {
        if (textItems[i - j].word === tailWords[tailWords.length - 1 - j])
          matches++;
      }
      if (matches >= threshold) {
        refinedRight = i;
        break;
      }
    }
  }

  return {
    start: textItems[refinedLeft].index,
    end: textItems[refinedRight].index,
  };
}
